import { ref, type Ref } from 'vue'

export type PopupId =
  | 'deleteDevice'
  | 'temporary'
  | 'meterNotReachable'
  | 'newVersion'
  | 'basePrice'
  | 'notificationsRequest'
  | 'confirmEmail'
  | 'afterTour'
  | 'cancelArticle'
  | 'cubeNotReachable'
  | 'afterTourExplanation'
  | 'tour'
  | 'chooseProvider'
  | 'newsletterRequest'

interface PopupState {
  isModal: boolean
  canBeClosedByUser: boolean
  overlayContext: 'global' | "inline" | "home"
  showOverlay: boolean | "content"
  visible: boolean | "isSimulationMeasure",
  lastFocussedElement?: HTMLElement
}

const activeModal: Ref<null | PopupId> = ref(null)
const popups: Ref<Record<PopupId, PopupState>> = ref({
  deleteDevice: {
    isModal: true,
    canBeClosedByUser: true,
    overlayContext: 'global',
    showOverlay: true,
    visible: false
  },
  temporary: {
    isModal: false,
    canBeClosedByUser: false,
    overlayContext: 'inline',
    showOverlay: false,
    visible: false
  },
  meterNotReachable: {
    isModal: true,
    canBeClosedByUser: false,
    overlayContext: 'global',
    showOverlay: false,
    visible: 'isSimulationMeasure'
  },
  newVersion: {
    isModal: true,
    canBeClosedByUser: false,
    overlayContext: 'global',
    showOverlay: true,
    visible: false
  },
  basePrice: {
    isModal: true,
    canBeClosedByUser: false,
    overlayContext: 'global',
    showOverlay: true,
    visible: false
  },
  notificationsRequest: {
    isModal: true,
    canBeClosedByUser: true,
    overlayContext: 'global',
    showOverlay: true,
    visible: false
  },
  confirmEmail: {
    isModal: true,
    canBeClosedByUser: false,
    overlayContext: 'global',
    showOverlay: true,
    visible: false
  },
  afterTour: {
    isModal: false,
    canBeClosedByUser: true,
    overlayContext: 'global',
    showOverlay: false,
    visible: false
  },
  cancelArticle: {
    isModal: true,
    canBeClosedByUser: true,
    overlayContext: 'global',
    showOverlay: true,
    visible: false
  },
  cubeNotReachable: {
    isModal: false,
    canBeClosedByUser: false,
    overlayContext: 'home',
    showOverlay: true,
    visible: false
  },
  afterTourExplanation: {
    isModal: false,
    canBeClosedByUser: true,
    overlayContext: 'global',
    showOverlay: false,
    visible: false
  },
  tour: {
    isModal: false,
    canBeClosedByUser: true,
    overlayContext: 'global',
    showOverlay: false,
    visible: true
  },
  chooseProvider: {
    isModal: false,
    canBeClosedByUser: true,
    overlayContext: 'global',
    showOverlay: 'content',
    visible: false
  },
  newsletterRequest: {
    isModal: false,
    canBeClosedByUser: true,
    overlayContext: 'global',
    showOverlay: 'content',
    visible: false
  }

  /*
  meterlessNote: {
    visible: 'isMeterless',
  },
  settingsMoved: {
    visible: true,
  },
  newsletterRequest: {
    visible: true,
    visibleValue: false,
  },
 */
} satisfies Record<PopupId, PopupState>)

export function usePopup() {
  let trapFocus = ref(false)

  const getPopup = (name: PopupId) => {
    if (!popups.value.hasOwnProperty(name)) {
      throw new Error(`Trying to get unknown popup "${name}".`)
    }
    return popups.value[name]
  }

  const showPopup = (name: PopupId) => {
    const popup = getPopup(name)

    console.debug(`Showing popup: ${name}`)

    if (popup.isModal) {
      popup.lastFocussedElement = document.activeElement as HTMLElement
      trapFocus.value = true
      activeModal.value = name
    }

    popup.visible = true
  }

  const hideActiveModal = () => {
    if (activeModal.value) {
      hidePopup(activeModal.value)
    }
  }

  const hidePopup = (name: PopupId) => {
    if (!popups.value.hasOwnProperty(name)) {
      throw Error(`Trying to hide popup "${name}" that does not exist.`)
    }

    console.debug(`Hiding popup: ${name}`)

    const popup = getPopup(name)
    popup.visible = false

    if (popup.isModal) {
      trapFocus.value = false
      activeModal.value = null

      // Using nextTick did not work here - the element never received focus.
      if (popup.lastFocussedElement) {
        setTimeout(() => {
          popup.lastFocussedElement?.focus()
        }, 10)
      }
    }
  }

  return {
    showPopup,
    hidePopup,
    trapFocus,
    hideActiveModal,
    getPopup,
    activeModal,
    popups
  }
}
